import * as THREE from "three";
import type {
  ArchDefinition,
  ComputedToothPlacement,
  PrescriptionItem,
} from "../../../types/dental";

export interface BuiltArchGroupResult {
  group: THREE.Group;
  toothCenters: Map<string, THREE.Vector3>;
  toothMeshes: Map<string, THREE.Mesh>;
  toothPlacements: Map<string, ComputedToothPlacement>;
  hingePoint: THREE.Vector3;
  hingeAxis: THREE.Vector3;
}

export function buildArchGroup(
  arch: ArchDefinition,
  curveColor: number,
  jawOffsetZ: number,
  selectedPrescriptionItem: PrescriptionItem | null,
  selectedToothId: string | null,
  selectableToothMeshes: THREE.Mesh[]
): BuiltArchGroupResult {
  const group = new THREE.Group();
  const toothCenters = new Map<string, THREE.Vector3>();
  const toothMeshes = new Map<string, THREE.Mesh>();
  const toothPlacements = new Map<string, ComputedToothPlacement>();

  const points = generateArchPoints(
    arch.width,
    arch.depth,
    arch.height,
    arch.toothCount,
    jawOffsetZ
  );

  const { hingePoint, hingeAxis } = computeArchHingeFromPoints(points);
  const toothIds = generateToothIds(arch.jawType, arch.toothCount);

  const highlightedTeeth =
    selectedPrescriptionItem?.target.jaw === arch.jawType
      ? new Set(selectedPrescriptionItem.target.teeth)
      : new Set<string>();

  const curveGeometry = new THREE.BufferGeometry().setFromPoints(points);
  const curveMaterial = new THREE.LineBasicMaterial({ color: curveColor });
  const curve = new THREE.Line(curveGeometry, curveMaterial);
  group.add(curve);

  const markerGeometry = new THREE.SphereGeometry(1.5, 12, 12);
  const markerMaterial = new THREE.MeshStandardMaterial({ color: 0x0f172a });

  const toothGeometry = new THREE.BoxGeometry(6, 10, 8);

  points.forEach((point, index) => {
    const toothId = toothIds[index];

    const marker = new THREE.Mesh(markerGeometry, markerMaterial);
    marker.position.copy(point);
    group.add(marker);

    const isPrescriptionHighlighted = highlightedTeeth.has(toothId);
    const isSelected = selectedToothId === toothId;

    const baseColor = arch.jawType === "upper" ? 0xe0f2fe : 0xdcfce7;

    const color = isSelected
      ? 0xf97316
      : isPrescriptionHighlighted
        ? 0xf59e0b
        : baseColor;

    const emissive = isSelected
      ? 0x9a3412
      : isPrescriptionHighlighted
        ? 0x7c2d12
        : 0x000000;

    const emissiveIntensity = isSelected
      ? 0.55
      : isPrescriptionHighlighted
        ? 0.35
        : 0;

    const toothMaterial = new THREE.MeshStandardMaterial({
      color,
      emissive,
      emissiveIntensity,
    });

    const tooth = new THREE.Mesh(toothGeometry, toothMaterial);

    const verticalOffset = arch.jawType === "upper" ? -5 : 5;
    tooth.position.set(point.x, point.y + verticalOffset, point.z);

    const tangent = getTangentAt(points, index);
    const angleY = Math.atan2(tangent.x, tangent.z);
    tooth.rotation.y = angleY;

    if (arch.jawType === "upper") {
      tooth.rotation.z = Math.PI;
    }

    tooth.userData.toothId = toothId;
    tooth.userData.jawType = arch.jawType;
    tooth.userData.isTooth = true;

    group.add(tooth);
    selectableToothMeshes.push(tooth);

    const center = tooth.position.clone();

    toothCenters.set(toothId, center);
    toothMeshes.set(toothId, tooth);
    toothPlacements.set(toothId, {
      toothId,
      jawType: arch.jawType,
      archPoint: [point.x, point.y, point.z],
      archTangent: [tangent.x, tangent.y, tangent.z],
      basePosition: [center.x, center.y, center.z],
      baseRotationDeg: [
        THREE.MathUtils.radToDeg(tooth.rotation.x),
        THREE.MathUtils.radToDeg(tooth.rotation.y),
        THREE.MathUtils.radToDeg(tooth.rotation.z),
      ],
    });
  });

  return {
    group,
    toothCenters,
    toothMeshes,
    toothPlacements,
    hingePoint,
    hingeAxis,
  };
}

export function generateArchPoints(
  archWidth: number,
  archDepth: number,
  archHeight: number,
  toothCount: number,
  jawOffsetZ: number
): THREE.Vector3[] {
  const points: THREE.Vector3[] = [];
  const halfWidth = archWidth / 2;
  const count = Math.max(toothCount, 2);

  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : i / (count - 1);
    const x = THREE.MathUtils.lerp(-halfWidth, halfWidth, t);
    const normalized = halfWidth === 0 ? 0 : x / halfWidth;
    const z = -(normalized * normalized) * archDepth + archDepth + jawOffsetZ;
    const y = archHeight;

    points.push(new THREE.Vector3(x, y, z));
  }

  return points;
}

export function generateToothIds(
  jaw: "upper" | "lower",
  toothCount: number
): string[] {
  const start = jaw === "upper" ? 1 : 17;
  return Array.from({ length: toothCount }, (_, i) => `T${start + i}`);
}

export function getTangentAt(
  points: THREE.Vector3[],
  index: number
): THREE.Vector3 {
  const prev = points[Math.max(index - 1, 0)];
  const next = points[Math.min(index + 1, points.length - 1)];
  return next.clone().sub(prev).normalize();
}

function computeArchHingeFromPoints(points: THREE.Vector3[]): {
  hingePoint: THREE.Vector3;
  hingeAxis: THREE.Vector3;
} {
  if (points.length < 2) {
    return {
      hingePoint: new THREE.Vector3(0, 0, 0),
      hingeAxis: new THREE.Vector3(1, 0, 0),
    };
  }

  const leftRear = points[0];
  const rightRear = points[points.length - 1];

  const hingeAxis = new THREE.Vector3()
    .subVectors(rightRear, leftRear)
    .normalize();

  const rearMid = new THREE.Vector3()
    .addVectors(leftRear, rightRear)
    .multiplyScalar(0.5);

  const hingePoint = new THREE.Vector3(
    rearMid.x,
    rearMid.y - 2,
    rearMid.z + 2
  );

  return { hingePoint, hingeAxis };
}

export function mergeToothCenters(
  target: Map<string, THREE.Vector3>,
  source: Map<string, THREE.Vector3>
): void {
  source.forEach((value, key) => {
    target.set(key, value);
  });
}

export function alignGroupXAxisToAxis(
  group: THREE.Group,
  axis: THREE.Vector3
): void {
  const xAxis = new THREE.Vector3(1, 0, 0);
  const target = axis.clone().normalize();

  const quaternion = new THREE.Quaternion().setFromUnitVectors(xAxis, target);
  group.quaternion.copy(quaternion);
}